import { useAuth } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { getAiJobSocket, type AiJobState } from '@/lib/aiJobSocket';

/** The resolved singleton socket returned by {@link getAiJobSocket}. */
type AiJobSocket = Awaited<ReturnType<typeof getAiJobSocket>>;

interface UseAiJobByJobIdResult {
  state: AiJobState | null;
  error: string | null;
}

/**
 * Subscribe to live updates for a specific AI job by its id.
 *
 * Used by the Generate dialog: once the controller returns a jobId, the
 * dialog passes it to this hook to track progress and react to terminal
 * statuses (auto-close on `completed`, surface the error message on
 * `failed`). Pass `null` to disable.
 *
 * @param jobId - The job id to subscribe to.
 */
export function useAiJobByJobId(
  jobId: string | null | undefined,
): UseAiJobByJobIdResult {
  const { getToken } = useAuth();
  const [state, setState] = useState<AiJobState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setState(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setState(null);
    setError(null);

    const onState = (next: AiJobState) => {
      if (cancelled) return;
      if (next.jobId !== jobId) return;
      setState(next);
    };

    // Join the job room and pull the current state. The server's join ack
    // replays the latest state from Redis (including a terminal
    // completed/failed), so a (re)joining client never misses the result.
    const join = (socket: AiJobSocket) => {
      socket.emit(
        'ai-job:join',
        { jobId },
        (response: { state?: AiJobState; error?: string }) => {
          if (cancelled) return;
          if (response?.error) {
            setError(response.error);
            return;
          }
          if (response?.state) setState(response.state);
        },
      );
    };

    // Re-join on every (re)connect. Socket.IO rooms are per-connection, so a
    // transient disconnect during a 30–60s+ job silently drops our membership;
    // without re-joining, the terminal `ai-job:state` emitted during the gap is
    // lost and the dialog spins forever (SUP-17). Re-joining re-reads the
    // persisted state, recovering the result the moment the socket is back.
    const onConnect = () => {
      if (cancelled) return;
      void getAiJobSocket(getToken).then((socket) => {
        if (!cancelled) join(socket);
      });
    };

    void (async () => {
      const socket = await getAiJobSocket(getToken);
      if (cancelled) return;

      socket.on('ai-job:state', onState);
      socket.on('connect', onConnect);

      // The singleton socket may already be connected (no future `connect`
      // event to lean on), so do the first join eagerly when it is.
      if (socket.connected) join(socket);
    })();

    return () => {
      cancelled = true;
      void getAiJobSocket(getToken).then((socket) => {
        socket.off('ai-job:state', onState);
        socket.off('connect', onConnect);
        socket.emit('ai-job:leave', { jobId });
      });
    };
  }, [jobId, getToken]);

  return { state, error };
}
